import { useState, useEffect } from 'react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Store, Sparkles } from 'lucide-react';

const storeNameSchema = z.object({
  storeName: z.string().trim().min(2, { message: 'Nome da loja deve ter pelo menos 2 caracteres' }).max(50, { message: 'Nome da loja deve ter no máximo 50 caracteres' })
});

export function StoreNameDialog() {
  const { store, refreshStore } = useAuth();
  const [open, setOpen] = useState(false);
  const [storeName, setStoreName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Show dialog if store exists and has the default name
    if (store && store.name === 'Minha Loja') {
      setOpen(true);
    }
  }, [store]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 30);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const result = storeNameSchema.safeParse({ storeName });
    if (!result.success) {
      setError(result.error.errors[0]?.message || 'Nome inválido');
      return;
    }

    if (!store) return;

    setLoading(true);

    const newSlug = `${generateSlug(storeName)}-${store.id.substring(0, 8)}`;

    const { error: updateError } = await supabase
      .from('stores')
      .update({
        name: storeName.trim(),
        slug: newSlug
      })
      .eq('id', store.id);

    if (updateError) {
      console.error('Error updating store:', updateError);
      toast.error('Erro ao salvar o nome da loja. Tente novamente.');
      setLoading(false);
      return;
    }

    // Close dialog immediately - realtime subscription will update the store
    setLoading(false);
    setOpen(false);
    toast.success('Bem-vindo(a) ao VesteIA! 🎉');
    
    // Fire-and-forget refresh as fallback (don't await)
    refreshStore().catch(console.error);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg">
            <Store className="w-8 h-8 text-primary-foreground" />
          </div>
          <DialogTitle className="text-2xl">Bem-vindo(a) ao VesteIA!</DialogTitle>
          <DialogDescription className="text-base">
            Para começar, qual é o nome da sua loja?
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="store-name-input">Nome da loja</Label>
            <Input
              id="store-name-input"
              type="text"
              placeholder="Ex: Boutique Fashion, Loja da Maria..."
              value={storeName}
              onChange={(e) => {
                setStoreName(e.target.value);
                setError('');
              }}
              disabled={loading}
              autoFocus
              className="text-base"
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>

          <Button type="submit" className="w-full" size="lg" disabled={loading || !storeName.trim()}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Começar a usar
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-center text-muted-foreground mt-2">
          Você pode alterar isso depois nas configurações
        </p>
      </DialogContent>
    </Dialog>
  );
}